import { doc, getDoc, setDoc } from "firebase/firestore";

import { firestore } from "../../config/ConfigFirebase";
import {
  CANCEL_NOTICE_TEXT_FIELDS,
  DEFAULT_PQRSD_CANCEL_NOTICE_TEXTS,
  normalizeCancelNoticeTexts,
} from "./gfunctions/handleCorrespondence/cancelNoticeEmail";

// Radicados module configuration. Lives in the shared `config` collection
// alongside config/global and config/pqrsd, following the same split: one doc
// per module. Read server-side too, so these field names and defaults MUST
// match the functions:
//   - ocrEnabled: gfunctions/handleUploadFile/index.js, at upload time;
//   - archiverCanCancel, cancelNotifications:
//     gfunctions/handleCorrespondence/index.js (DEFAULT_CANCEL_CONFIG), when a
//     radicado is annulled.
const CONFIG = "config";
const FILED_DOC = "filed";

export const DEFAULT_CANCEL_NOTIFICATIONS = {
  enabled: true,
  recipients: true,
  carbonCopy: true,
  senders: false,
  includeReason: true,
  // A PQRSD's citizen gets the PQRSD email (tracking page link) instead.
  pqrsdCitizen: true,
  // The emails' editable copy, filled with the defaults in cancelNoticeEmail.js.
  texts: normalizeCancelNoticeTexts(),
  pqrsdTexts: normalizeCancelNoticeTexts(undefined, DEFAULT_PQRSD_CANCEL_NOTICE_TEXTS),
};

export const DEFAULT_FILED_CONFIG = {
  // Document AI text extraction on upload. Its output (documentText) is what
  // the radicados search matches on, so turning this off stops new radicados
  // from becoming searchable by their content. Default true — that is what the
  // module did before the switch existed, and an absent doc must not change
  // behaviour.
  ocrEnabled: true,
  // Whether the ARCHIVER role may annul radicados. When off it binds every
  // archiver, even one holding filed.cancel; ADMIN always can.
  archiverCanCancel: true,
  // Who is emailed when a finalized radicado is annulled.
  cancelNotifications: DEFAULT_CANCEL_NOTIFICATIONS,
};

const boolOr = (value, fallback) => (typeof value === "boolean" ? value : fallback);

const BOOLEAN_NOTICE_KEYS = ["enabled", "pqrsdCitizen", "recipients", "carbonCopy", "senders", "includeReason"];

// What the screen saves for a text set: as typed (trimmed, capped). An optional
// text saved empty is how the admin removes that line, so it must not become
// the default here.
const textsToStore = (texts) =>
  CANCEL_NOTICE_TEXT_FIELDS.reduce((acc, { key, max }) => {
    const value = texts?.[key];
    acc[key] = typeof value === "string" ? value.trim().slice(0, max) : "";
    return acc;
  }, {});

// Stored values over the defaults, keeping only booleans: the functions ignore
// anything else, so the screen must not show a switch in a state they don't read.
export const normalizeFiledConfig = (stored = {}) => {
  const notices = stored.cancelNotifications && typeof stored.cancelNotifications === "object"
    ? stored.cancelNotifications
    : {};
  return {
    ...stored,
    ocrEnabled: boolOr(stored.ocrEnabled, DEFAULT_FILED_CONFIG.ocrEnabled),
    archiverCanCancel: boolOr(stored.archiverCanCancel, DEFAULT_FILED_CONFIG.archiverCanCancel),
    cancelNotifications: {
      ...BOOLEAN_NOTICE_KEYS.reduce((acc, key) => {
        acc[key] = boolOr(notices[key], DEFAULT_CANCEL_NOTIFICATIONS[key]);
        return acc;
      }, {}),
      texts: normalizeCancelNoticeTexts(notices.texts),
      pqrsdTexts: normalizeCancelNoticeTexts(notices.pqrsdTexts, DEFAULT_PQRSD_CANCEL_NOTICE_TEXTS),
    },
  };
};

export const getFiledConfig = async () => {
  try {
    const snap = await getDoc(doc(firestore, CONFIG, FILED_DOC));
    return normalizeFiledConfig(snap.exists() ? snap.data() : {});
  } catch (e) {
    console.error("getFiledConfig error:", e);
    return null;
  }
};

export const saveFiledConfig = async (values) => {
  try {
    const notices = values.cancelNotifications || {};
    await setDoc(
      doc(firestore, CONFIG, FILED_DOC),
      {
        ocrEnabled: !!values.ocrEnabled,
        archiverCanCancel: !!values.archiverCanCancel,
        cancelNotifications: {
          ...BOOLEAN_NOTICE_KEYS.reduce((acc, key) => {
            acc[key] = !!notices[key];
            return acc;
          }, {}),
          texts: textsToStore(notices.texts),
          pqrsdTexts: textsToStore(notices.pqrsdTexts),
        },
      },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.error("saveFiledConfig error:", e);
    return null;
  }
};
